import { z } from 'zod'
import { source, writeBack } from '@/kinds/git/schema'

// Mirrors internal/kinds/agentrun. Two providers write it: `factory` (an agent
// session of the software factory, docs/factory.md) and `sdlc` (the retired bridge).

export const PHASES = ['Pending', 'Starting', 'Working', 'Waiting', 'Suspended', 'Ended', 'Failed'] as const
export type Phase = (typeof PHASES)[number]

/** Whether a factory session is finished for good. */
export const isOver = (phase: string) => phase === 'Ended' || phase === 'Failed'

/** Whether a factory session has a sandbox up (or on its way up). */
export const isLive = (phase: string) =>
  phase === 'Pending' || phase === 'Starting' || phase === 'Working' || phase === 'Waiting'

export const runReport = z
  .object({
    headline: z.string(),
    outcome: z.string(),
    cause: z.string().optional(),
    evidence: z
      .array(
        z.object({ what: z.string(), query: z.string().optional(), excerpt: z.string().optional() }).strict(),
      )
      .optional(),
    change: z.array(z.object({ file: z.string(), why: z.string() }).strict()).optional(),
    risk: z.string().optional(),
    riskNote: z.string().optional(),
    verify: z.array(z.string()).optional(),
    confidence: z.string().optional(),
  })
  .strict()
export type RunReport = z.infer<typeof runReport>

export const runMessage = z
  .object({
    seq: z.number().int(),
    at: z.string().optional(),
    role: z.string(),
    text: z.string(),
    /** names of the pictures a user line carried, "<seq>-<n>.<ext>"; never bytes (fetched through api.factoryPicture) */
    pictures: z.array(z.string()).max(4).optional(),
  })
  .strict()
export type RunMessage = z.infer<typeof runMessage>

export const runWhatsNew = z.object({ title: z.string(), explainer: z.string().optional() }).strict()
export type RunWhatsNew = z.infer<typeof runWhatsNew>

/** One screenshot pair: blob ids (no before for a page the base did not have) and the factory's file names. */
export const runShot = z
  .object({
    name: z.string(),
    caption: z.string().optional(),
    viewport: z.enum(['phone', 'desktop']),
    before: z.string().optional(),
    after: z.string(),
    before_file: z.string().optional(),
    after_file: z.string().optional(),
  })
  .strict()
export type RunShot = z.infer<typeof runShot>

export const agentRunSpec = z
  .object({
    source,
    issue_id: z.string().optional(),
    project_id: z.string().default(''),
    title: z.string().default(''),
    repo_url: z.string().default(''),
    status: z.string(),
    pr_url: z.string().default(''),
    workspace: z.string().default(''),
    started_at: z.string().default(''),
    updated_at: z.string().default(''),
    events: z
      .array(
        z.object({ ts: z.string().optional(), kind: z.string().optional(), detail: z.string().optional() }),
      )
      .default([]),
    // factory sessions
    name: z.string().optional(),
    template: z.string().optional(),
    branch: z.string().optional(),
    base: z.string().optional(),
    message: z.string().optional(),
    source_kind: z.string().optional(),
    source_ref: z.string().optional(),
    pr_number: z.number().int().min(0).optional(),
    pr_state: z.string().optional(),
    last_active_at: z.string().optional(),
    ended_at: z.string().optional(),
    cost: z
      .object({
        usd: z.string().optional(),
        turns: z.number().int().optional(),
        notional: z.boolean().optional(),
      })
      .strict()
      .optional(),
    report: runReport.optional(),
    messages: z.array(runMessage).max(200).optional(),
    /** the agent session (Prime's thread, or a chat) that started it, woken when it finishes */
    started_by: z.string().optional(),
    /** the agent's account of the change for the owner (publish.json), for What's new */
    whatsnew: runWhatsNew.optional(),
    /** its before and after screenshots, blob ids (docs/factory.md "Screenshots and What's new") */
    shots: z.array(runShot).max(6).optional(),
    shots_at: z.string().optional(),
  })
  .strict()
export type AgentRunSpec = z.infer<typeof agentRunSpec>

export const agentRunStatus = z.object({
  writebacks: z.array(writeBack).optional(),
  writeback_error: z.string().optional(),
  pushed_for: z.string().optional(),
  completed_task: z.string().optional(),
  /** how much of the session's cost the stats have recorded */
  counted: z.object({ usd: z.string().optional(), turns: z.number().int().optional() }).optional(),
  /** the task the owner (or Prime) tied the session to (link_task) */
  task_id: z.string().optional(),
  /** the lead was told this session's pull request merged */
  told_merged: z.boolean().optional(),
  /** the base-branch commit carrying a pull request Forgejo closed rather than merged (its change landed another way) */
  landed: z.string().optional(),
  /** the closed pull request ("#n") the watcher looked for on the base branch, so it looks once */
  landed_looked: z.string().optional(),
  /** pages shown beside the lead's thread, newest first (show_artifact) */
  artifacts: z.array(z.object({ id: z.string(), at: z.string(), seen: z.string().optional() })).optional(),
  last_touched: z.string(),
})
export type AgentRunStatus = z.infer<typeof agentRunStatus>
